import type { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { defaultCompensationMethods } from "@redotech/redo-return-core-common/dropoff-provider";
import {
  DEFAULT_DROPOFF_RADIUS_MILES,
  type DropoffProviderType,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import type { DropoffProviderSettings } from "./returns-settings";

/** Matches the `existing?.enabled ?? true` hydration in shipping/transforms.ts. */
export const DROPOFF_ENABLED_DEFAULT = true;

export interface DropoffAutoProcessEntry {
  provider: DropoffProviderType;
  methods: CompensationMethod[];
}

export interface DropoffEnabledAndRadiusEntry {
  provider: DropoffProviderType;
  enabled: boolean;
  radiusMiles: number;
}

/** Writes only the auto-process methods, owned by Automations → Processing. */
export function mergeDropoffAutoProcessMethods(
  stored: DropoffProviderSettings[] | undefined,
  entries: DropoffAutoProcessEntry[],
): DropoffProviderSettings[] {
  return mergeByProvider(stored, entries, (entry, base) => ({
    ...base,
    eligibleAutoProcessCompensationMethods: entry.methods,
  }));
}

/** Writes only `enabled` and `radiusMiles`, owned by the Shipping page. */
export function mergeDropoffEnabledAndRadius(
  stored: DropoffProviderSettings[] | undefined,
  entries: DropoffEnabledAndRadiusEntry[],
): DropoffProviderSettings[] {
  return mergeByProvider(stored, entries, (entry, base) => ({
    ...base,
    enabled: entry.enabled,
    radiusMiles: entry.radiusMiles,
  }));
}

/**
 * One array, two owning pages: a save rewrites only its own fields on top of
 * what is stored, so fields absent from the type — like `fee` — survive.
 */
function mergeByProvider<Entry extends { provider: DropoffProviderType }>(
  stored: DropoffProviderSettings[] | undefined,
  entries: Entry[],
  applyOwnedFields: (
    entry: Entry,
    base: DropoffProviderSettings,
  ) => DropoffProviderSettings,
): DropoffProviderSettings[] {
  const byProvider = new Map(
    (stored ?? []).map((dropoff) => [dropoff.provider, dropoff]),
  );
  for (const entry of entries) {
    const base = byProvider.get(entry.provider) ?? unsetDropoff(entry.provider);
    byProvider.set(entry.provider, applyOwnedFields(entry, base));
  }
  return [...byProvider.values()];
}

/** What a provider looks like before either page has stored anything for it. */
function unsetDropoff(provider: DropoffProviderType): DropoffProviderSettings {
  return {
    provider,
    enabled: DROPOFF_ENABLED_DEFAULT,
    radiusMiles: DEFAULT_DROPOFF_RADIUS_MILES,
    eligibleAutoProcessCompensationMethods:
      defaultCompensationMethods(provider),
  };
}
